import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { WebService } from './web/web.service';

export type SendgridSlotStatus = 'configured' | 'pending' | 'empty' | 'orphan';

export interface SendgridTemplate {
  id: string;
  name: string;
  versionCount: number;
  updatedAt: string;
  thumbnailTone: 'navy' | 'teal' | 'purple' | 'coral' | 'blue';
  thumbnailUrl?: string | null;
}

export interface SendgridTemplateDetails extends SendgridTemplate {
  versions: SendgridTemplateVersion[];
}

export interface SendgridTemplateVersion {
  id: string;
  templateId: string;
  name: string;
  subject: string;
  updatedAt: string;
  editor: string;
  active: boolean;
  html: string;
  thumbnailUrl?: string | null;
}

export interface SendgridSlotDefinition {
  key: string;
  label: string;
}

export interface SendgridSlotAssignment {
  key: string;
  label: string;
  versionId: string | null;
  status: SendgridSlotStatus;
}

export interface SendgridPartnerConfiguration {
  partnerId: number | string;
  templateId: string | null;
  verifiedEmail: string | null;
  lastSyncAt: string | null;
  slots: SendgridSlotAssignment[];
}

export interface SendgridVerifiedSender {
  id: string | number;
  nickname: string;
  fromEmail: string;
  fromName: string;
  replyTo: string;
  verified: boolean;
}

@Injectable({ providedIn: 'root' })
export class SendgridTemplatesService {
  private readonly useSendgridTemplatesMock = true;
  private readonly useRealTemplateList = true;
  private readonly useRealPartnerSettings = true;
  private realTemplatesCache: SendgridTemplateDetails[] = [];
  private lastPartnerSettingsByPartnerId: Record<string, any> = {};

  readonly slots: SendgridSlotDefinition[] = [
    { key: 'sendgrid_onboarding_user_welcome_version', label: 'Bienvenida con credenciales - Onboarding' },
    { key: 'sendgrid_onboarding_email_suscription_template_version', label: 'Invitación a suscribirse' },
    { key: 'sendgrid_onboarding_email_validate_pin_template_version', label: 'Código de verificación (PIN)' },
    { key: 'sendgrid_onboarding_email_notify_partner_subscription_version', label: 'Aviso de alta - Interno' },
    { key: 'sendgrid_onboarding_email_notify_cancelled_subscription_version', label: 'Aviso de baja - Interno' },
    { key: 'sendgrid_recover_password_version', label: 'Recuperación de contraseña' },
    { key: 'sendgrid_one_time_payment_template_version', label: 'Envío de link de cobro' },
    { key: 'sendgrid_suscribe_version', label: 'Confirmación de cobro - Suscripción' },
    { key: 'sendgrid_registry_version', label: 'Bienvenida con credenciales - Suscripción' }
  ];

  private readonly settingsFieldBySlotKey: Record<string, string> = {
    sendgrid_onboarding_user_welcome_version: 'sendgridOnboardingUserWelcomeVersion',
    sendgrid_onboarding_email_suscription_template_version: 'sendgridOnboardingEmailSuscriptionTemplateVersion',
    sendgrid_onboarding_email_validate_pin_template_version: 'sendgridOnboardingEmailValidatePinTemplateVersion',
    sendgrid_onboarding_email_notify_partner_subscription_version: 'sendgridOnboardingEmailNotifyPartnerSubscriptionVersion',
    sendgrid_onboarding_email_notify_cancelled_subscription_version: 'sendgridOnboardingEmailNotifyCancelledSubscriptionVersion',
    sendgrid_recover_password_version: 'sendgridRecoverPasswordVersion',
    sendgrid_one_time_payment_template_version: 'sendgridOneTimePaymentTemplateVersion',
    sendgrid_suscribe_version: 'sendgridSuscribeVersion',
    sendgrid_registry_version: 'sendgridRegistryVersion'
  };

  private mockConfiguration: SendgridPartnerConfiguration = {
    partnerId: 0,
    templateId: null,
    verifiedEmail: null,
    lastSyncAt: new Date(Date.now() - 3 * 60000).toISOString(),
    slots: this.slots.map(slot => ({ key: slot.key, label: slot.label, versionId: null, status: 'empty' }))
  };

  private readonly mockVerifiedSenders: SendgridVerifiedSender[] = [
    {
      id: 1,
      nickname: 'Billcentrix',
      fromEmail: 'info@billcentrix.com',
      fromName: 'Billcentrix',
      replyTo: 'support@billcentrix.com',
      verified: true
    },
    {
      id: 2,
      nickname: 'Operaciones',
      fromEmail: 'ops@billcentrix.com',
      fromName: 'Operaciones Billcentrix',
      replyTo: 'ops@billcentrix.com',
      verified: false
    }
  ];

  private readonly mockTemplates: SendgridTemplate[] = [
    { id: 'd-indalo-7a8f5e07fde', name: 'Indalo Water', versionCount: 5, updatedAt: '2026-03-09T10:30:00Z', thumbnailTone: 'navy' },
    { id: 'd-volve-5f293be672', name: 'Volve', versionCount: 3, updatedAt: '2026-06-20T11:00:00Z', thumbnailTone: 'teal' },
    { id: 'd-coop-42e2025', name: 'Cooperativa de Ahorro y Crédito Empresarial', versionCount: 5, updatedAt: '2025-09-02T09:00:00Z', thumbnailTone: 'blue' },
    { id: 'd-innova-529c2fe818', name: 'Innovaciones Cloud', versionCount: 4, updatedAt: '2025-09-02T09:30:00Z', thumbnailTone: 'purple' },
    { id: 'd-fortis-14jul2026', name: 'Fortis Seguros', versionCount: 6, updatedAt: '2026-07-14T14:00:00Z', thumbnailTone: 'coral' },
    { id: 'd-divenue-2f4d9b9', name: 'Divenue Corporation', versionCount: 4, updatedAt: '2026-01-28T08:15:00Z', thumbnailTone: 'navy' }
  ];

  private readonly mockVersions: SendgridTemplateVersion[] = this.mockTemplates.flatMap(template => [
    {
      id: `${template.id}-v1`,
      templateId: template.id,
      name: 'Onboarding - Nuevo cliente de Alta',
      subject: `Bienvenido a ${template.name}`,
      updatedAt: '2026-05-12T09:20:00Z',
      editor: 'Ana Muñoz',
      active: false,
      html: this.buildMockHtml(template.name, 'Bienvenido a nuestro portal', 'Tu cuenta fue creada correctamente.')
    },
    {
      id: `${template.id}-v2`,
      templateId: template.id,
      name: 'Customer Registration Confirmation',
      subject: 'Suscripción activa',
      updatedAt: '2026-05-18T11:40:00Z',
      editor: 'Marwin Campos',
      active: false,
      html: this.buildMockHtml(template.name, 'Suscripción confirmada', 'Tu suscripción quedó activa.')
    },
    {
      id: `${template.id}-v3`,
      templateId: template.id,
      name: 'PIN - Verificación two factors',
      subject: 'Código de verificación para iniciar sesión',
      updatedAt: '2026-05-20T13:15:00Z',
      editor: 'Sofolink',
      active: true,
      html: this.buildMockHtml(template.name, 'Código {{pin}}', 'Usa este código para validar tu identidad.')
    },
    {
      id: `${template.id}-v4`,
      templateId: template.id,
      name: 'Cliente dado de baja',
      subject: 'Confirmación de cancelación',
      updatedAt: '2026-06-02T16:10:00Z',
      editor: 'Marwin Campos',
      active: false,
      html: this.buildMockHtml(template.name, 'Suscripción cancelada', 'La cancelación fue registrada.')
    }
  ]);

  constructor(private webService: WebService) {}

  getPartnerConfiguration(partnerId: number | string): Observable<SendgridPartnerConfiguration> {
    if (this.useSendgridTemplatesMock && !this.useRealPartnerSettings) {
      return of(this.clone({ ...this.mockConfiguration, partnerId })).pipe(delay(250));
    }

    return this.webService.get(`${this.webService.HOST}/api/v1/partners/${partnerId}/sendgrid-settings`)
      .pipe(map(response => {
        const settings = response?.result || response || {};
        this.lastPartnerSettingsByPartnerId[String(partnerId)] = this.clone(settings);
        return this.mapPartnerConfigurationResponse(settings, partnerId);
      }));
  }

  listVerifiedSenders(): Observable<SendgridVerifiedSender[]> {
    if (this.useSendgridTemplatesMock && !this.useRealPartnerSettings) {
      return of(this.clone(this.mockVerifiedSenders)).pipe(delay(250));
    }

    return this.webService.get(`${this.webService.HOST}/api/v1/sendgrid/verified-senders?refresh=true`)
      .pipe(map(response => this.mapVerifiedSendersResponse(response)));
  }

  listTemplates(): Observable<SendgridTemplate[]> {
    if (this.useSendgridTemplatesMock && !this.useRealTemplateList) {
      return of(this.clone(this.mockTemplates)).pipe(delay(250));
    }

    return this.webService.get(`${this.webService.HOST}/api/v1/sendgrid/templates?generations=dynamic&pageSize=100`)
      .pipe(map(response => {
        this.realTemplatesCache = this.mapTemplateSearchResponse(response);
        return this.realTemplatesCache.map(({ versions, ...template }) => template);
      }));
  }

  getTemplate(templateId: string): Observable<SendgridTemplateDetails> {
    if (this.useRealTemplateList) {
      return this.webService.get(`${this.webService.HOST}/api/v1/sendgrid/templates/${templateId}`)
        .pipe(map(response => {
          const template = this.mapTemplateDetailResponse(response);
          this.upsertTemplateCache(template);
          return template;
        }));
    }

    if (this.useSendgridTemplatesMock) {
      const template = this.mockTemplates.find(item => item.id === templateId);
      const versions = this.mockVersions.filter(version => version.templateId === templateId);
      return template ? of(this.clone({ ...template, versions })).pipe(delay(200)) : throwError(() => 'Plantilla no encontrada');
    }

    return this.webService.get(`${this.webService.HOST}/sendgrid/templates/${templateId}`)
      .pipe(map(response => response.result || response));
  }

  selectVersionForField(
    partnerId: number | string,
    fieldName: string,
    templateId: string,
    versionId: string
  ): Observable<SendgridPartnerConfiguration> {
    if (this.useSendgridTemplatesMock) {
      this.mockConfiguration.partnerId = partnerId;
      this.mockConfiguration.templateId = templateId;
      this.mockConfiguration.slots = this.mockConfiguration.slots.map(slot => {
        if (slot.key !== fieldName) {
          return slot;
        }

        return {
          ...slot,
          versionId,
          status: 'configured'
        };
      });

      return of(this.clone(this.mockConfiguration)).pipe(delay(250));
    }

    return this.webService.post({
      fieldName,
      templateId,
      versionId
    }, `${this.webService.HOST}/sendgrid/templates/partner/${partnerId}/field-version`)
      .pipe(map(response => response.result || response));
  }

  savePartnerConfiguration(
    partnerId: number | string,
    configuration: SendgridPartnerConfiguration
  ): Observable<SendgridPartnerConfiguration> {
    if (this.useSendgridTemplatesMock && !this.useRealPartnerSettings) {
      this.mockConfiguration = this.clone({
        ...configuration,
        slots: configuration.slots.map(slot => ({ ...slot, status: slot.versionId ? 'configured' : 'empty' }))
      });
      return of(this.clone(this.mockConfiguration)).pipe(delay(250));
    }

    const payload = this.buildPartnerSettingsPayload(partnerId, configuration);
    return this.webService.put(payload, `${this.webService.HOST}/api/v1/partners/${partnerId}/sendgrid-settings`)
      .pipe(map(response => {
        const settings = response?.result || (response?.sendgridTemplateId ? response : payload);
        this.lastPartnerSettingsByPartnerId[String(partnerId)] = this.clone(settings);
        return this.mapPartnerConfigurationResponse(settings, partnerId);
      }));
  }

  private buildMockHtml(partnerName: string, title: string, body: string): string {
    return `
      <html>
        <body style="font-family: Arial, sans-serif; color: #17213a; margin: 0; padding: 32px; background: #f4f6fa;">
          <main style="max-width: 620px; margin: 0 auto; background: #fff; border-radius: 12px; overflow: hidden;">
            <header style="background: #0F1E3A; color: #fff; padding: 24px;">
              <strong>${partnerName}</strong>
            </header>
            <section style="padding: 28px;">
              <h1 style="margin-top: 0;">${title}</h1>
              <p>${body}</p>
              <p>Hola {{customer_name}}, esta plantilla mantiene visibles las variables Handlebars.</p>
            </section>
          </main>
        </body>
      </html>`;
  }

  private mapTemplateSearchResponse(response: any): SendgridTemplateDetails[] {
    const templates = response?.result?.result || response?.result || response || [];
    if (!Array.isArray(templates)) {
      return [];
    }

    return templates.map((template: any, index: number) => {
      const versions = Array.isArray(template.versions) ? template.versions : [];
      const mappedVersions = versions.map((version: any) => this.mapTemplateVersion(version, template.id));

      return {
        id: template.id,
        name: template.name,
        versionCount: mappedVersions.length,
        updatedAt: this.normalizeDate(template.updated_at),
        thumbnailTone: this.getThumbnailTone(index),
        thumbnailUrl: this.normalizeThumbnailUrl(mappedVersions.find(version => version.thumbnailUrl)?.thumbnailUrl),
        versions: mappedVersions
      };
    });
  }

  private mapTemplateDetailResponse(response: any): SendgridTemplateDetails {
    const template = response?.result || response;
    const mappedTemplates = this.mapTemplateSearchResponse({ result: { result: [template] } });
    return mappedTemplates[0];
  }

  private mapPartnerConfigurationResponse(response: any, partnerId: number | string): SendgridPartnerConfiguration {
    const settings = response?.result || response || {};

    return {
      partnerId: settings.partnerId || settings.partner_id || partnerId,
      templateId: settings.sendgridTemplateId || settings.templateId || null,
      verifiedEmail: settings.verifiedEmail || settings.verified_email || null,
      lastSyncAt: this.normalizeDate(settings.updatedAt || settings.updated_at || null),
      slots: this.slots.map(slot => {
        const fieldName = this.settingsFieldBySlotKey[slot.key];
        const versionId = fieldName ? settings[fieldName] || null : null;

        return {
          key: slot.key,
          label: slot.label,
          versionId,
          status: versionId ? 'configured' : 'empty'
        };
      })
    };
  }

  private buildPartnerSettingsPayload(partnerId: number | string, configuration: SendgridPartnerConfiguration): any {
    const cachedSettings = this.lastPartnerSettingsByPartnerId[String(partnerId)] || {};
    const payload = {
      ...cachedSettings,
      sendgridTemplateId: configuration.templateId,
      verifiedEmail: configuration.verifiedEmail
    };

    configuration.slots.forEach(slot => {
      const fieldName = this.settingsFieldBySlotKey[slot.key];
      if (fieldName) {
        payload[fieldName] = slot.versionId;
      }
    });

    return payload;
  }

  private mapVerifiedSendersResponse(response: any): SendgridVerifiedSender[] {
    const senders = response?.result?.senders || response?.result?.result || response?.result || response || [];
    if (!Array.isArray(senders)) {
      return [];
    }

    return senders
      .map((sender: any) => ({
        id: sender.id || sender.senderId || sender.sender_id || sender.fromEmail || sender.from_email,
        nickname: sender.nickname || sender.nickName || sender.name || sender.fromName || sender.from_name || sender.fromEmail || sender.from_email || '',
        fromEmail: sender.fromEmail || sender.from_email || sender.email || '',
        fromName: sender.fromName || sender.from_name || sender.name || '',
        replyTo: sender.replyTo || sender.reply_to || sender.replyToEmail || sender.reply_to_email || '',
        verified: sender.verified === true || sender.verified === 1 || sender.verifiedStatus === true || sender.verified_status === true
      }))
      .filter((sender: SendgridVerifiedSender) => !!sender.fromEmail)
      .sort((a: SendgridVerifiedSender, b: SendgridVerifiedSender) => Number(b.verified) - Number(a.verified));
  }

  private mapTemplateVersion(version: any, fallbackTemplateId: string): SendgridTemplateVersion {
    return {
      id: version.id,
      templateId: version.template_id || fallbackTemplateId,
      name: this.cleanVersionName(version.name),
      subject: version.subject || '',
      updatedAt: this.normalizeDate(version.updated_at),
      editor: version.editor || '',
      active: version.active === true || version.active === 1,
      html: version.html_content || '',
      thumbnailUrl: this.normalizeThumbnailUrl(version.thumbnail_url)
    };
  }

  private cleanVersionName(value: string | null | undefined): string {
    if (!value) {
      return '';
    }

    return value
      .replace(/\s*\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z/g, '')
      .replace(/\s{2,}/g, ' ')
      .trim();
  }

  private normalizeDate(value: string | null | undefined): string {
    if (!value) {
      return '';
    }
    return value.includes('T') ? value : value.replace(' ', 'T');
  }

  private normalizeThumbnailUrl(value: string | null | undefined): string | null {
    if (!value) {
      return null;
    }
    return value.startsWith('//') ? `https:${value}` : value;
  }

  private getThumbnailTone(index: number): SendgridTemplate['thumbnailTone'] {
    const tones: SendgridTemplate['thumbnailTone'][] = ['navy', 'teal', 'blue', 'purple', 'coral'];
    return tones[index % tones.length];
  }

  private upsertTemplateCache(template: SendgridTemplateDetails): void {
    const index = this.realTemplatesCache.findIndex(item => item.id === template.id);
    if (index >= 0) {
      this.realTemplatesCache[index] = template;
      return;
    }
    this.realTemplatesCache.push(template);
  }

  private clone<T>(value: T): T {
    return JSON.parse(JSON.stringify(value));
  }
}
